/**
 * @file usePlanningControl.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-23
 * @description One planner at a time on a planning board (Cam, 15 Oct 2026),
 *              for the board screen that calls it. Opening the board asks the
 *              server for it (POST /planning/control/:board/claim): it is this
 *              tab's when free, else the planner is asked. Then it keeps in
 *              touch — every 2 s while this tab plans (the heartbeat, which
 *              also brings a request in), every 1 s while it waits for an
 *              answer, every 3 s otherwise (5 s for someone who cannot plan,
 *              60 s while the database has no control table). Leaving the
 *              board lets it go (a keepalive request, so a closed tab does
 *              too). Every decision — the 8 s answer window included — is the
 *              server's; this only shows it.
 *
 *              Returns { ready, view, role, holds, blockReason, notice,
 *              receivedAt, request(), answer('leave'|'busy'), refresh() }.
 *              Before the migration `ready` is false and `blockReason` null:
 *              the board works as it did.
 *
 *              1.1.0 (2026-10-23, Cam): control is per board DAY. The hook
 *              takes the day the board shows (`date`, 'YYYY-MM-DD'); every
 *              call names it; moving to another day lets the old day go (a
 *              release — to whoever is waiting for it) and claims the new
 *              one. Until the new day's answer arrives the board is view only
 *              ("Checking who is planning …"), so nothing is written to a day
 *              this tab may not hold. An answer about another day than the
 *              one shown is ignored.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import {
  PLANNING_TAB_ID, PLANNING_CONTROL_EVENT, setActivePlanningBoard, controlBlockReason, controlNotice
} from '../utils/planningControl';

const INTERVAL = { holder: 2000, requester: 1000, viewer: 3000, watcher: 5000, notReady: 60000 };

export default function usePlanningControl(board, { canPlan = false, date = null } = {}) {
  const [view, setView] = useState(null);
  const [receivedAt, setReceivedAt] = useState(() => Date.now());
  const dateRef = useRef(date);
  dateRef.current = date;
  const alive = useRef(true);
  // Answers can overtake each other: only the newest request's is shown.
  const issued = useRef(0);
  const applied = useRef(0);

  const url = `${API_URL}/api/transport/planning/control/${board}`;

  const apply = useCallback((data, mySeq) => {
    if (!alive.current || mySeq < applied.current) return;
    // An answer about a day this board has moved off.
    if (data && data.perDay && data.date && dateRef.current && data.date !== dateRef.current) return;
    applied.current = mySeq;
    if (!data || data.success === false || data.ready !== true) {
      setView({ ready: false });
    } else {
      setView(data);
    }
    setReceivedAt(Date.now());
  }, []);

  const call = useCallback(async (action, body = {}) => {
    issued.current += 1;
    const mySeq = issued.current;
    try {
      const day = dateRef.current || null;
      const res = action === 'view'
        ? await fetch(`${url}?tabId=${encodeURIComponent(PLANNING_TAB_ID)}${day ? `&date=${encodeURIComponent(day)}` : ''}`, { credentials: 'include' })
        : await fetch(`${url}/${action}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tabId: PLANNING_TAB_ID, date: day, ...body })
        });
      const data = await res.json().catch(() => null);
      apply(data, mySeq);
      return data;
    } catch (error) {
      // Offline for a moment: keep what we had; the next poll tries again.
      return null;
    }
  }, [url, apply]);

  const claim = useCallback(() => call('claim', { date: dateRef.current || null }), [call]);
  const poll = useCallback(() => (canPlan ? call('heartbeat') : call('view')), [call, canPlan]);

  // The board this tab is on, and its day: requests to /api/transport carry them.
  useEffect(() => {
    alive.current = true;
    setActivePlanningBoard(board, date);
    return () => { alive.current = false; setActivePlanningBoard(null); };
  }, [board, date]);

  // Opening the board's day: ask for it (or just look, without the
  // permission). Another day: this one is let go first.
  useEffect(() => {
    const day = date || null;
    if (canPlan) claim(); else call('view');
    const leave = () => {
      if (!canPlan) return;
      try {
        fetch(`${url}/release`, {
          method: 'POST', credentials: 'include', keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tabId: PLANNING_TAB_ID, date: day })
        }).catch(() => {});
      } catch (_) { /* leaving anyway */ }
    };
    window.addEventListener('pagehide', leave);
    return () => { window.removeEventListener('pagehide', leave); leave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, canPlan, date]);

  // Keeping in touch, faster while it matters.
  const role = view && view.ready ? view.role : null;
  useEffect(() => {
    if (!view) return undefined;
    const wait = !view.ready ? INTERVAL.notReady
      : !canPlan ? INTERVAL.watcher
      : (INTERVAL[role] || INTERVAL.viewer);
    const t = setTimeout(() => {
      // Became ready (the migration ran) while open: ask for the board then.
      if (!view.ready && canPlan) claim(); else poll();
    }, wait);
    return () => clearTimeout(t);
  }, [view, role, canPlan, claim, poll]);

  // A write refused for control, or the tab coming back: look again now.
  useEffect(() => {
    const now = () => { if (view) poll(); };
    const onVisible = () => { if (document.visibilityState === 'visible') now(); };
    window.addEventListener(PLANNING_CONTROL_EVENT, now);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(PLANNING_CONTROL_EVENT, now);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [view, poll]);

  const ready = !!(view && view.ready);
  // The answer we hold is about another day than the one shown: view only
  // until the new day's arrives (it is on its way — the claim above).
  const shown = ready && view.perDay && date && view.date !== date
    ? { ...view, date, pending: true, role: 'viewer', request: null, notice: null } : view;
  const shownRole = shown && shown.ready ? shown.role : null;
  return {
    ready,
    view: shown,
    role: shownRole,
    receivedAt,
    holds: ready && shownRole === 'holder',
    blockReason: canPlan ? controlBlockReason(shown, board) : null,
    notice: controlNotice(shown, board, { canPlan }),
    request: claim,
    answer: (reply) => call('answer', { answer: reply }),
    refresh: poll
  };
}
